import { useState } from 'react'
import { ApiError } from '../api/client'
import { useAttachChecklist, type Plan } from '../features/plans/api'
import { useChecklistTemplates } from '../features/templates/checklistApi'
import { selectable } from '../features/templates/selectable'

/**
 * Adds a published checklist template's items to a plan that already exists.
 *
 * This is what keeps a timeline-only plan from being a one-way door. Templates
 * already on the plan are left out of the list: attaching one twice is refused
 * by the server, and offering it invites the refusal.
 */
export function AttachChecklist({ plan }: { plan: Plan }) {
  const { data: checklists } = useChecklistTemplates()
  const attach = useAttachChecklist(plan.eventId)
  const [selected, setSelected] = useState('')
  const [error, setError] = useState<string | null>(null)

  const attachedNames = new Set(plan.checklistSources.map((source) => source.name))
  const available = selectable(checklists).filter((item) => !attachedNames.has(item.name))

  if (available.length === 0) {
    return (
      <p className="hint">
        {plan.checklistSources.length > 0
          ? 'Every published checklist template is already on this plan.'
          : 'No published checklist templates to attach.'}
      </p>
    )
  }

  return (
    <div className="attach-checklist">
      <label htmlFor="attach-checklist-select">Add a checklist</label>
      <div className="form-actions">
        <select
          id="attach-checklist-select"
          value={selected}
          onChange={(event) => setSelected(event.target.value)}
        >
          <option value="">Choose a checklist template…</option>
          {available.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name} (v{template.version}, {template.itemCount} items)
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!selected || attach.isPending}
          onClick={() => {
            setError(null)
            attach
              .mutateAsync({ checklistTemplateId: selected })
              .then(() => setSelected(''))
              .catch((err: unknown) =>
                setError(
                  err instanceof ApiError
                    ? err.detail
                    : 'That checklist could not be attached.',
                ),
              )
          }}
        >
          {attach.isPending ? 'Attaching…' : 'Attach checklist'}
        </button>
      </div>
      <p className="hint">
        Its items are copied onto the plan as they are published now. Later edits to the template
        will not change them.
      </p>
      {error ? (
        <p role="alert" className="error">
          {error}
        </p>
      ) : null}
    </div>
  )
}
